// useFeesData.ts
// --------------
// Sprint 5b — data hook for the Fees page. Co-locates everything the
// orchestrator needs to read or mutate against the store + the late-fee
// policy endpoint, behind one stable shape:
//
//   const data = useFeesData();
//
// The orchestrator never touches `useCondoStore`, `useAuthStore`, or
// `apiService` directly. Three benefits:
//
//   1. Re-renders shrink. Each call site here is a narrow Zustand selector
//      (Sprint 5 / 5.6 pattern). Page rerenders only when its slice
//      changes, not on every owner row mutation.
//   2. Mocking gets cheap. A unit test can mock `useFeesData` to return a
//      fixed shape instead of stubbing four different stores + an HTTP
//      client.
//   3. Late-fee policy fetch + setters live next to the data they touch
//      instead of being inlined in the page render body.
//
// What stays in the orchestrator: modal open/close UI state, form
// instances. That's correct — those are presentational concerns.

import { useCallback, useEffect, useState } from 'react';
import { useCondoStore } from '../../store/condoStore';
import { useAuthStore } from '../../store/authStore';
import { apiService } from '../../services/api';
import { Fee, LateFeePolicy, Owner } from '../../types';

export interface UseFeesData {
  // ── Read ───────────────────────────────────────────────────────────────
  fees: Fee[];
  owners: Owner[];
  currentCondoId: string | undefined;

  // Late-fee policy — fetched once per condoId, updated via mutations below.
  lateFeePolicy: LateFeePolicy | null;
  policyLoading: boolean;
  policySaving: boolean;
  policyDeleting: boolean;

  // ── Mutations on fees ─────────────────────────────────────────────────
  createFee: (fee: Omit<Fee, 'id'>) => Promise<unknown>;
  updateFeeApi: (id: string, fee: Partial<Fee>) => Promise<unknown>;
  deleteFeeApi: (id: string) => Promise<unknown>;
  /** Sprint 135 (R12-2) — remove every per-owner row of a condo-wide assessment. */
  deleteFeeGroupApi: (groupId: string) => Promise<unknown>;
  markFeeAsPaidApi: (id: string) => Promise<unknown>;
  bulkCreateFees: (req: any) => Promise<unknown>;

  // Waiver: separate from updateFeeApi because the backend has a dedicated
  // /waive endpoint and we want a clean message hook for it.
  waiveFee: (feeId: string, reason: string) => Promise<void>;

  // Force a refetch into the Zustand store. Use this after a backend
  // mutation that doesn't auto-update the store (e.g. waiver).
  refetchFees: () => Promise<unknown> | void;

  // ── Mutations on the late-fee policy ──────────────────────────────────
  savePolicy: (values: Partial<LateFeePolicy>) => Promise<LateFeePolicy | null>;
  deletePolicy: () => Promise<void>;
}

/** The hook. */
export function useFeesData(): UseFeesData {
  // Narrow selectors — never destructure the whole store.
  const fees             = useCondoStore((s: any) => s.fees) as Fee[];
  const owners           = useCondoStore((s: any) => s.owners) as Owner[];
  const createFee        = useCondoStore((s: any) => s.createFee);
  const updateFeeApi     = useCondoStore((s: any) => s.updateFeeApi);
  const deleteFeeApi      = useCondoStore((s: any) => s.deleteFeeApi);
  const deleteFeeGroupApi = useCondoStore((s: any) => s.deleteFeeGroupApi);
  const markFeeAsPaidApi = useCondoStore((s: any) => s.markFeeAsPaidApi);
  const bulkCreateFees   = useCondoStore((s: any) => s.bulkCreateFees);
  const fetchFeesAction  = useCondoStore((s: any) => s.fetchFees);

  const currentCondo     = useAuthStore((s) => s.currentCondo);
  const currentCondoId   = currentCondo?.id;

  // ── Late-fee policy local state ────────────────────────────────────────
  // Kept here (not in the store) because nothing else in the app reads it
  // today. If a second consumer appears, hoist this to the Zustand store.
  const [lateFeePolicy, setLateFeePolicy] = useState<LateFeePolicy | null>(null);
  const [policyLoading,  setPolicyLoading]  = useState(false);
  const [policySaving,   setPolicySaving]   = useState(false);
  const [policyDeleting, setPolicyDeleting] = useState(false);

  // Fetch the policy when the active condo changes. Idempotent — the API
  // cache plus our own state mean a refire on the same id is cheap.
  useEffect(() => {
    if (!currentCondoId) return;
    let cancelled = false;
    setPolicyLoading(true);
    apiService.getLateFeePolicy(currentCondoId)
      .then((policy) => { if (!cancelled) setLateFeePolicy(policy); })
      .catch(() => { if (!cancelled) setLateFeePolicy(null); })
      .finally(() => { if (!cancelled) setPolicyLoading(false); });
    return () => { cancelled = true; };
  }, [currentCondoId]);

  const savePolicy = useCallback(async (values: Partial<LateFeePolicy>) => {
    if (!currentCondoId) return null;
    setPolicySaving(true);
    try {
      const saved = await apiService.upsertLateFeePolicy(currentCondoId, values as any);
      setLateFeePolicy(saved);
      return saved;
    } finally {
      setPolicySaving(false);
    }
  }, [currentCondoId]);

  const deletePolicy = useCallback(async () => {
    if (!currentCondoId) return;
    setPolicyDeleting(true);
    try {
      await apiService.deleteLateFeePolicy(currentCondoId);
      setLateFeePolicy(null);
    } finally {
      setPolicyDeleting(false);
    }
  }, [currentCondoId]);

  const waiveFee = useCallback(async (feeId: string, reason: string) => {
    if (!currentCondoId) return;
    await apiService.waiveFee(feeId, reason);
    // Waiver doesn't auto-mutate the Zustand fees list; refetch so the
    // table updates from PENDING/OVERDUE -> WAIVED.
    if (typeof fetchFeesAction === 'function') {
      await fetchFeesAction(currentCondoId);
    }
  }, [currentCondoId, fetchFeesAction]);

  const refetchFees = useCallback(() => {
    if (!currentCondoId || typeof fetchFeesAction !== 'function') return;
    return fetchFeesAction(currentCondoId);
  }, [currentCondoId, fetchFeesAction]);

  return {
    fees,
    owners,
    currentCondoId,
    lateFeePolicy,
    policyLoading,
    policySaving,
    policyDeleting,
    createFee,
    updateFeeApi,
    deleteFeeApi,
    deleteFeeGroupApi,
    markFeeAsPaidApi,
    bulkCreateFees,
    waiveFee,
    refetchFees,
    savePolicy,
    deletePolicy,
  };
}
